import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, UserCheck, CheckSquare, FolderKanban, Building2, GitFork, Clock,
  ShieldCheck, Calendar, Activity, BarChart3, Bell, Settings, LogOut, Menu, X,
  TrendingUp, AlertTriangle, Gauge, CalendarDays, GanttChartSquare,
  Sparkles, MessageSquare, SlidersHorizontal, Star, Layers, ChevronLeft, ChevronRight, Building,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import api from "../lib/api";
import { initials } from "../lib/ui";
import OnboardingPanel from "./OnboardingPanel";
import NavSettings from "./NavSettings";
import { applyTypography } from "../lib/typography";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, id: "tasks" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
  { to: "/projects", label: "Projects", icon: FolderKanban, id: "projects" },
  { to: "/workspaces", label: "Workspaces", icon: Layers, id: "workspaces" },
  { to: "/my-work", label: "My Work", icon: UserCheck, id: "my_work" },
  { to: "/favourites", label: "My Favourites", icon: Star, id: "favourites" },
  { to: "/timeline", label: "Timeline", icon: GanttChartSquare, id: "timeline", roles: ["master_admin", "admin", "manager"] },
  { to: "/follow-ups", label: "Follow-Ups", icon: Clock, id: "follow_ups" },
  { to: "/departments", label: "Departments", icon: Building2, id: "departments" },
  { to: "/dependencies", label: "Dependencies", icon: GitFork, id: "dependencies" },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, id: "approvals" },
  { to: "/escalations", label: "Escalations", icon: TrendingUp, id: "escalations" },
  { to: "/registers", label: "Risks & Issues", icon: AlertTriangle, id: "registers" },
  { to: "/meetings", label: "Meetings", icon: Calendar, id: "meetings" },
  { to: "/performance", label: "Performance", icon: Gauge, id: "performance", roles: ["master_admin", "admin", "manager"] },
  { to: "/command-center", label: "Command Center", icon: Activity, id: "command_center" },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "reports" },
  { to: "/intelligence", label: "Intelligence", icon: Sparkles, id: "intelligence", roles: ["master_admin", "admin", "manager"] },
  { to: "/assistant", label: "Talk to Task Matrix", icon: MessageSquare, id: "assistant" },
  { to: "/notifications", label: "Notifications", icon: Bell, id: "notifications" },
  { to: "/account-settings", label: "Account Settings", icon: Building, id: "account_settings", roles: ["master_admin", "admin"] },
  { to: "/admin", label: "Admin", icon: Settings, id: "admin", roles: ["master_admin", "admin"] },
];

function mergeNav(roleNav, prefs) {
  if (!prefs || !Array.isArray(prefs.items) || prefs.items.length === 0) return roleNav;
  const byId = Object.fromEntries(roleNav.map((n) => [n.id, n]));
  const out = [];
  prefs.items.forEach((p) => {
    const base = byId[p.id];
    if (base) out.push({ ...base, label: p.label || base.label, hidden: !!p.hidden });
  });
  roleNav.forEach((n) => { if (!out.find((o) => o.id === n.id)) out.push(n); });
  return out;
}

function BellDropdown({ unread, onChanged, placement }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/notifications", { params: { unread_only: true } }).then((r) => setItems((r.data || []).slice(0, 8))).catch(() => {});
  }, [open]);
  const openNotif = async (n) => {
    setOpen(false);
    try { await api.post(`/notifications/${n.id}/read`); } catch {}
    onChanged?.();
    if (n.link_entity === "task" && n.link_id) return nav(`/tasks?open=${n.link_id}`);
    if (n.link_entity === "workspace" && n.link_id) {
      const [wsid, thread] = String(n.link_id).split("#");
      return nav(`/workspaces/${wsid}${thread ? `?thread=${thread}` : ""}`);
    }
    nav("/notifications");
  };
  return (
    <div className="relative">
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
      <button data-testid="bell-button" onClick={() => setOpen((s) => !s)} className={`relative p-2 rounded-lg ${placement === "up" ? "text-slate-400 hover:text-white hover:bg-[#1E293B]" : "text-slate-600 hover:bg-slate-100"}`} title="Notifications">
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="bell-badge" className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[9px] rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="bell-dropdown" className={`absolute ${placement === "up" ? "bottom-full mb-2" : "top-full mt-2"} right-0 w-80 bg-white rounded-xl border shadow-xl z-50 text-slate-800 overflow-hidden`}>
          <div className="px-3 py-2 border-b text-xs font-semibold text-slate-500 flex justify-between">
            <span>Unread notifications</span>
            <button data-testid="bell-view-all" onClick={() => { setOpen(false); nav("/notifications"); }} className="text-blue-600 hover:underline">View all</button>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y">
            {items.length === 0 && <div className="p-4 text-sm text-slate-400">You're all caught up.</div>}
            {items.map((n) => (
              <button key={n.id} data-testid={`bell-item-${n.id}`} onClick={() => openNotif(n)} className="w-full text-left px-3 py-2 hover:bg-slate-50">
                <div className="text-sm font-medium text-slate-800 truncate">{n.title}</div>
                <div className="text-xs text-slate-500 truncate">{n.message}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [showNavSettings, setShowNavSettings] = useState(false);
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navPrefs, setNavPrefs] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("tm_sidebar_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed((c) => { const v = !c; localStorage.setItem("tm_sidebar_collapsed", v ? "1" : "0"); return v; });

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { unread_only: true } });
      setUnread(data.length);
    } catch {}
  };
  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/auth/preferences/nav").then((r) => setNavPrefs(r.data?.nav || null)).catch(() => {});
  }, []);

  const [brand, setBrand] = useState(null);
  useEffect(() => {
    api.get("/branding/public").then((r) => setBrand(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/settings").then((r) => applyTypography(r.data?.typography)).catch(() => applyTypography(null));
  }, []);
  const brandLogo = brand?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}` : null;
  const brandName = brand?.product_name || "Task Matrix";
  const brandTagline = brand?.tagline || "Tasks. Ownership. Execution.";

  const roleNav = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));
  const navItems = mergeNav(roleNav, navPrefs);
  const visibleNav = navItems.filter((n) => !n.hidden);
  const horizontal = navPrefs?.orientation === "horizontal";

  const Brand = ({ compact }) => (
    <div className={`flex items-center gap-2 ${compact ? "" : "px-5"} ${!compact && collapsed ? "md:px-0 md:justify-center" : ""}`}>
      {brandLogo
        ? <img src={brandLogo} alt={brandName} className="h-8 w-8 rounded-lg object-contain bg-white shrink-0" data-testid="sidebar-logo" />
        : <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center font-bold text-white text-sm tracking-tight shrink-0">TM</div>}
      <div className={!compact && collapsed ? "md:hidden" : ""}>
        <div className={`font-semibold tracking-tight leading-none ${compact ? "text-slate-900" : "text-slate-100"}`}>{brandName}</div>
        <div className="text-[10px] text-blue-400 mt-0.5 tracking-wide">{brandTagline}</div>
      </div>
    </div>
  );

  const VerticalItem = ({ n }) => (
    <NavLink
      key={n.id}
      to={n.to}
      data-testid={`nav-${n.id}`}
      title={n.label}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${collapsed ? "md:justify-center md:px-0" : ""} ${
          isActive ? "bg-[#1E293B] text-white font-medium" : "text-slate-300 hover:bg-[#1E293B]/60 hover:text-white"
        }`
      }
    >
      <n.icon size={17} className="shrink-0" />
      <span className={collapsed ? "md:hidden" : ""}>{n.label}</span>
      {n.id === "notifications" && unread > 0 && (
        <span className={`ml-auto bg-blue-600 text-white text-[10px] rounded-full px-1.5 py-0.5 ${collapsed ? "md:hidden" : ""}`}>{unread}</span>
      )}
    </NavLink>
  );

  const HorizontalItem = ({ n }) => (
    <NavLink
      key={n.id}
      to={n.to}
      data-testid={`nav-${n.id}`}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] whitespace-nowrap transition-colors ${
          isActive ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-100"
        }`
      }
    >
      <n.icon size={15} />
      {n.label}
      {n.id === "notifications" && unread > 0 && (
        <span className="bg-blue-600 text-white text-[10px] rounded-full px-1.5 py-0.5">{unread}</span>
      )}
    </NavLink>
  );

  return (
    <div className={`flex ${horizontal ? "flex-col" : ""} h-screen bg-[#F8FAFC] overflow-hidden`}>
      <OnboardingPanel />
      {/* Sidebar: primary nav in vertical mode; mobile drawer in both modes */}
      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} ${horizontal ? "md:hidden" : "md:translate-x-0 md:static"} fixed z-40 w-64 ${collapsed ? "md:w-16" : "md:w-64"} h-full bg-[#0F172A] text-slate-100 flex flex-col transition-all duration-200`}
        data-testid="sidebar"
      >
        {!horizontal && (
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            className="hidden md:flex absolute -right-3 top-20 z-50 h-6 w-6 items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-600 hover:text-slate-900 hover:shadow-lg transition-shadow"
          >
            {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>
        )}
        <div className="h-16 flex items-center gap-2 border-b border-slate-800">
          <Brand />
          <button className="ml-auto md:hidden px-2" onClick={() => setMobileOpen(false)}><X size={18} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {visibleNav.map((n) => <VerticalItem key={n.id} n={n} />)}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className={`flex items-center gap-2 px-2 py-2 rounded-md ${collapsed ? "md:flex-col md:gap-3 md:px-0" : ""}`}>
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-xs font-semibold shrink-0">
              {initials(user?.full_name)}
            </div>
            <div className={`min-w-0 flex-1 ${collapsed ? "md:hidden" : ""}`}>
              <div className="text-sm font-medium truncate">{user?.full_name}</div>
              <div className="text-[10px] text-slate-400 capitalize">{user?.role?.replace("_", " ")}</div>
            </div>
            <BellDropdown unread={unread} onChanged={loadUnread} placement="up" />
            <button data-testid="nav-settings-button" title="Navigation settings" onClick={() => setShowNavSettings(true)} className="text-slate-400 hover:text-white">
              <SlidersHorizontal size={15} />
            </button>
            <button data-testid="logout-button" onClick={logout} className="text-slate-400 hover:text-white">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Mobile menu button (vertical mode has no top bar) */}
      {!horizontal && !mobileOpen && (
        <button
          data-testid="mobile-menu-button"
          onClick={() => setMobileOpen(true)}
          className="md:hidden fixed top-3 left-3 z-30 p-2 rounded-lg bg-[#0F172A] text-white shadow-lg"
        >
          <Menu size={18} />
        </button>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {horizontal && (
          <div className="h-14 flex items-center gap-3 px-4 md:px-6 bg-white/95 backdrop-blur-md border-b border-slate-200 z-20 sticky top-0" data-testid="topbar-horizontal">
            <button className="md:hidden text-slate-600" onClick={() => setMobileOpen(true)}><Menu size={20} /></button>
            <div className="hidden md:flex items-center gap-4 min-w-0 flex-1">
              <Brand compact />
              <nav className="flex items-center gap-1 overflow-x-auto" data-testid="horizontal-nav">
                {visibleNav.map((n) => <HorizontalItem key={n.id} n={n} />)}
              </nav>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <BellDropdown unread={unread} onChanged={loadUnread} placement="down" />
              <button data-testid="nav-settings-button-top" title="Navigation settings" onClick={() => setShowNavSettings(true)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600">
                <SlidersHorizontal size={17} />
              </button>
              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-xs font-semibold text-white">
                {initials(user?.full_name)}
              </div>
              <button data-testid="logout-button" onClick={logout} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Logout">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        )}
        <main className={`flex-1 overflow-y-auto ${!horizontal ? "pt-14 md:pt-0" : ""}`}>{children}</main>
      </div>

      {showNavSettings && (
        <NavSettings
          items={navItems}
          orientation={navPrefs?.orientation || "vertical"}
          onClose={() => setShowNavSettings(false)}
          onSaved={(prefs) => setNavPrefs(prefs)}
        />
      )}
    </div>
  );
}
